import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Sentry } from '@/services/sentry';
import { tokenStorage } from '@/services/tokenStorage';
import api from '@/services/api';
import i18n from '@/i18n';

// Guards to prevent duplicate setup
let listenersInitialized = false;
let appStateListenerInitialized = false;

function extractLinkFromNotification(notification) {
  const data = notification?.data;

  if (!data) {
    return null;
  }

  // FCM format (Android, and FCM-based iOS)
  if (data.link) {
    return data.link;
  }

  // APNs format (iOS via Customer.io)
  if (data.CIO?.push?.link) {
    return data.CIO.push.link;
  }

  return null;
}

/**
 * Composable for managing push notifications
 * Handles registration, permissions, and token management
 */
export function usePushNotifications() {
  /**
   * Check if push notifications are supported on this platform
   */
  const isSupported = () => {
    return Capacitor.isNativePlatform();
  };

  /**
   * Check current notification permission status
   */
  const checkPermissions = async () => {
    if (!isSupported()) {
      return {
        receive: 'denied',
      };
    }

    try {
      const result = await PushNotifications.checkPermissions();

      console.log('[Push] Current permission status:', result.receive);

      return result;
    } catch (error) {
      console.error('[Push] Error checking permissions:', error);
      Sentry.captureException(error);

      throw error;
    }
  };

  /**
   * Request notification permissions from the user
   */
  const requestPermissions = async () => {
    if (!isSupported()) {
      throw new Error(i18n.global.t('pushNotifications.notSupported'));
    }

    try {
      console.log('[Push] Requesting push notification permissions...');

      const result = await PushNotifications.requestPermissions();

      console.log('[Push] Permission result:', result.receive);

      return result;
    } catch (error) {
      console.error('[Push] Error requesting permissions:', error);
      Sentry.captureException(error);

      throw error;
    }
  };

  /**
   * Register device for push notifications
   * Only call this after permissions are granted
   */
  const register = async () => {
    if (!isSupported()) {
      throw new Error(i18n.global.t('pushNotifications.notSupported'));
    }

    try {
      console.log('[Push] Registering for push notifications...');

      await PushNotifications.register();

      console.log('[Push] Registration initiated');
    } catch (error) {
      console.error('[Push] Error registering for push notifications:', error);
      Sentry.captureException(error);

      throw error;
    }
  };

  /**
   * Send device token to backend
   * Called automatically by the registration listener
   */
  const sendTokenToBackend = async (token) => {
    try {
      // Check if user is authenticated
      const refreshToken = await tokenStorage.getRefreshToken();

      if (!refreshToken) {
        console.log('[Push] ⚠️ User not authenticated, skipping token send');

        return false;
      }

      await api.post('/api/account/device-token', {
        token: token,
        platform: Capacitor.getPlatform()
      });

      console.log('[Push] ✅ Device token sent to backend successfully');

      return true;
    } catch (error) {
      console.error('[Push] ❌ Failed to send device token to backend:', error);
      Sentry.captureException(error);

      return false;
    }
  };

  /**
   * Setup push notification listeners
   * Should be called once during app initialization, BEFORE any registration calls
   */
  const setupListeners = ({ onNotificationTap } = {}) => {
    if (!isSupported()) {
      return;
    }

    // Prevent duplicate listener setup within the same JS context
    if (listenersInitialized) {
      console.log('[Push] Listeners already initialized, skipping');

      return;
    }

    listenersInitialized = true;

    console.log('[Push] Setting up push notification listeners...');

    // Listen for successful registration
    PushNotifications.addListener('registration', async (tokenData) => {
      console.log('[Push] Push registration success!');
      console.log('[Push] Device token:', tokenData.value);

      await sendTokenToBackend(tokenData.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Push registration error:', error);
      Sentry.captureException(error);
    });

    // Listen for push notification received (when app is in foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Push notification received:', notification);
    });

    // Listen for push notification action performed (user tapped notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Push] Push notification action performed:', action);

      const link = extractLinkFromNotification(action.notification);

      if (link && onNotificationTap) {
        console.log('[Push] Notification link found:', link);

        onNotificationTap(link);
      }
    });
  };

  /**
   * Setup app state listener to detect when user returns from Settings
   * Automatically registers if permissions were just granted
   */
  const setupAppStateListener = () => {
    if (!isSupported()) {
      return;
    }

    // Prevent duplicate listener setup
    if (appStateListenerInitialized) {
      console.log('[Push] App state listener already initialized, skipping');

      return;
    }

    appStateListenerInitialized = true;

    CapApp.addListener('appStateChange', async (state) => {
      console.log('[Push] App state changed:', state.isActive ? 'foreground' : 'background');

      // When app returns to foreground, check if notifications were just enabled
      if (state.isActive) {
        const refreshToken = await tokenStorage.getRefreshToken();

        if (!refreshToken) {
          console.log('[Push] User not authenticated, skipping permission check');

          return;
        }

        console.log('[Push] Checking if notification permissions changed...');

        const permStatus = await PushNotifications.checkPermissions();

        console.log('[Push] Permission status:', permStatus.receive);

        if (permStatus.receive === 'granted') {
          console.log('[Push] Notifications enabled! Registering for push...');

          await register();
        }
      }
    });
  };

  /**
   * Initialize push notifications for authenticated users
   * Checks permissions and registers if already granted
   * If permission was never requested on this device, prompts the user
   */
  const initializeForAuthenticatedUser = async (onboardingCompleted) => {
    if (!isSupported()) {
      return;
    }

    try {
      console.log('[Push] Initializing push notifications for authenticated user...');

      const permissionStatus = await checkPermissions();

      if (permissionStatus.receive === 'granted') {
        console.log('[Push] Permission already granted, registering...');

        await register();
      } else if (permissionStatus.receive === 'prompt') {
        if (!onboardingCompleted) {
          console.log('[Push] User not onboarded yet, skipping push registration');

          return;
        }

        // Never asked on this device - request permission and register
        console.log('[Push] Permission not yet requested on this device, prompting user...');

        await requestAndRegister();
      } else {
        console.log('[Push] Permission denied by user');
      }
    } catch (error) {
      console.error('[Push] ❌ Error during push notification initialization:', error);
      Sentry.captureException(error);
    }
  };

  /**
   * Request permissions and register (used during onboarding)
   * Returns true if successful, false otherwise
   */
  const requestAndRegister = async () => {
    try {
      // First, check current permission status
      let permissionStatus = await checkPermissions();

      // If permission not yet determined, request it
      if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
        permissionStatus = await requestPermissions();
      }

      // If granted, register for push notifications
      if (permissionStatus.receive === 'granted') {
        await register();

        return true;
      } else if (permissionStatus.receive === 'denied') {
        console.log('[Push] ❌ Push notifications permission denied');

        return false;
      }

      return false;
    } catch (error) {
      console.error('[Push] ❌ Error during request and register:', error);
      Sentry.captureException(error);

      return false;
    }
  };

  return {
    isSupported,
    checkPermissions,
    requestPermissions,
    register,
    setupListeners,
    setupAppStateListener,
    initializeForAuthenticatedUser,
    requestAndRegister,
  };
}
